/**
 * Custom Validation Hook
 * Comprehensive form validation with security features
 */

import { useState, useCallback } from 'react';
import { isValidOptionalPhone } from '../utils/phoneValidation';
// Inline validation functions
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePassword = (password) => {
  return {
    isValid: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
};

const validatePhoneNumber = (phone) => isValidOptionalPhone(phone);

const validateUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  return input.trim().replace(/[<>]/g, '');
};

const sanitizeFormData = (formData) => {
  const sanitized = {};
  Object.keys(formData).forEach(key => {
    sanitized[key] = sanitizeInput(formData[key]);
  });
  return sanitized;
};

export const useValidation = () => {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Clear specific field error
  const clearFieldError = useCallback((fieldName) => {
    setErrors(prev => {
      const { [fieldName]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Clear all errors
  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Mark field as touched
  const markFieldTouched = useCallback((fieldName) => {
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  }, []);

  // Validate single field (real-time validation)
  const validateFieldRealTime = useCallback((fieldName, value, rules = {}) => {
    const fieldErrors = [];

    // Required validation
    if (rules.required && (!value || value.toString().trim() === '')) {
      fieldErrors.push(`${rules.label || fieldName} is required`);
    }

    // Skip other validations if field is empty and not required
    if (!value || value.toString().trim() === '') {
      setErrors(prev => ({ ...prev, [fieldName]: fieldErrors }));
      return fieldErrors.length === 0;
    }

    // Email validation
    if (rules.type === 'email' && !validateEmail(value)) {
      fieldErrors.push('Please enter a valid email address');
    }

    // Password validation
    if (rules.type === 'password') {
      const passwordValidation = validatePassword(value);
      if (!passwordValidation.isValid) {
        fieldErrors.push(...Object.values(passwordValidation.errors).filter(error => error));
      }
    }

    // Phone validation
    if (rules.type === 'phone' && !validatePhoneNumber(value)) {
      fieldErrors.push('Please enter a valid phone number');
    }

    // URL validation
    if (rules.type === 'url' && !validateUrl(value)) {
      fieldErrors.push('Please enter a valid URL');
    }

    // Min length validation
    if (rules.minLength && value.length < rules.minLength) {
      fieldErrors.push(`${rules.label || fieldName} must be at least ${rules.minLength} characters`);
    }

    // Max length validation
    if (rules.maxLength && value.length > rules.maxLength) {
      fieldErrors.push(`${rules.label || fieldName} must be no more than ${rules.maxLength} characters`);
    }

    // Pattern validation
    if (rules.pattern && !rules.pattern.test(value)) {
      fieldErrors.push(rules.patternMessage || `${rules.label || fieldName} format is invalid`);
    }

    // Custom validation
    if (rules.custom && typeof rules.custom === 'function') {
      const customError = rules.custom(value);
      if (customError) {
        fieldErrors.push(customError);
      }
    }

    // Must be true validation (for checkboxes)
    if (rules.mustBeTrue && !value) {
      fieldErrors.push(`${rules.label || fieldName} must be checked`);
    }

    // Update errors state
    setErrors(prev => ({ ...prev, [fieldName]: fieldErrors }));
    return fieldErrors.length === 0;
  }, []);

  // Validate single field
  const validateField = useCallback((fieldName, value, rules = {}) => {
    const fieldErrors = [];

    // Required validation
    if (rules.required && (!value || value.toString().trim() === '')) {
      fieldErrors.push(`${rules.label || fieldName} is required`);
    }

    // Skip other validations if field is empty and not required
    if (!value || value.toString().trim() === '') {
      return fieldErrors;
    }

    // Email validation
    if (rules.type === 'email' && !validateEmail(value)) {
      fieldErrors.push('Please enter a valid email address');
    }

    // Password validation
    if (rules.type === 'password') {
      const passwordValidation = validatePassword(value);
      if (!passwordValidation.isValid) {
        fieldErrors.push(...Object.values(passwordValidation.errors).filter(error => error));
      }
    }

    // Phone validation
    if (rules.type === 'phone' && !validatePhoneNumber(value)) {
      fieldErrors.push('Please enter a valid phone number');
    }

    // URL validation
    if (rules.type === 'url' && !validateUrl(value)) {
      fieldErrors.push('Please enter a valid URL');
    }

    // Min length validation
    if (rules.minLength && value.length < rules.minLength) {
      fieldErrors.push(`${rules.label || fieldName} must be at least ${rules.minLength} characters`);
    }

    // Max length validation
    if (rules.maxLength && value.length > rules.maxLength) {
      fieldErrors.push(`${rules.label || fieldName} must be no more than ${rules.maxLength} characters`);
    }

    // Pattern validation
    if (rules.pattern && !rules.pattern.test(value)) {
      fieldErrors.push(rules.patternMessage || `${rules.label || fieldName} format is invalid`);
    }

    // Custom validation
    if (rules.custom && typeof rules.custom === 'function') {
      const customError = rules.custom(value);
      if (customError) {
        fieldErrors.push(customError);
      }
    }

    return fieldErrors;
  }, []);

  // Validate entire form
  const validateForm = useCallback((formData, validationRules) => {
    const newErrors = {};
    let isValid = true;

    for (const [fieldName, rules] of Object.entries(validationRules)) {
      const value = formData[fieldName];
      const fieldErrors = validateField(fieldName, value, rules);
      
      if (fieldErrors.length > 0) {
        newErrors[fieldName] = fieldErrors[0]; // Show first error
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  }, [validateField]);


  // Sanitize form data
  const sanitizeForm = useCallback((formData) => {
    return sanitizeFormData(formData);
  }, []);

  // Get field error
  const getFieldError = useCallback((fieldName) => {
    return errors[fieldName] || null;
  }, [errors]);

  // Check if field has error
  const hasFieldError = useCallback((fieldName) => {
    return !!errors[fieldName];
  }, [errors]);

  // Check if field is touched
  const isFieldTouched = useCallback((fieldName) => {
    return !!touched[fieldName];
  }, [touched]);

  // Check if field should show error
  const shouldShowFieldError = useCallback((fieldName) => {
    return isFieldTouched(fieldName) && hasFieldError(fieldName);
  }, [isFieldTouched, hasFieldError]);

  // Validate and sanitize input
  const validateAndSanitizeInput = useCallback((fieldName, value, rules = {}) => {
    // Sanitize input
    const sanitizedValue = typeof value === 'string' ? sanitizeInput(value) : value;
    
    // Validate sanitized input
    const isValid = validateFieldRealTime(fieldName, sanitizedValue, rules);
    
    return {
      value: sanitizedValue,
      isValid,
      error: getFieldError(fieldName)
    };
  }, [validateFieldRealTime, getFieldError]);

  // Common validation rules
  const commonRules = {
    email: {
      type: 'email',
      required: true,
      label: 'Email'
    },
    password: {
      type: 'password',
      required: true,
      label: 'Password'
    },
    confirmPassword: (password) => ({
      required: true,
      label: 'Confirm Password',
      custom: (value) => value !== password ? 'Passwords do not match' : null
    }),
    phone: {
      type: 'phone',
      required: true,
      label: 'Phone Number'
    },
    url: {
      type: 'url',
      required: false,
      label: 'URL'
    },
    required: (label) => ({
      required: true,
      label
    }),
    optional: (label) => ({
      required: false,
      label
    })
  };

  return {
    errors,
    touched,
    clearFieldError,
    clearAllErrors,
    markFieldTouched,
    validateField,
    validateFieldRealTime,
    validateForm,
    sanitizeForm,
    getFieldError,
    hasFieldError,
    isFieldTouched,
    shouldShowFieldError,
    validateAndSanitizeInput,
    commonRules
  };
};

export default useValidation;
